import React, { useEffect, useRef, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { formatNumber } from "../../utils/formatter";
import Loading from "../Loading";
import InfoModal from "../InfoModal";
import SelectBox from "../SelectBox";
import { useDispatch, useSelector } from "react-redux";
import { setChecked } from "../../redux/reducers/emissionSwitchChecked";
import pattern from "patternomaly";


ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

/* ------------------------------------------------------------------ */
/*  Fixed, colour-blind safe palette (Okabe–Ito)                       */
/* ------------------------------------------------------------------ */
const POLLUTANT_COLORS = {
  "CH4": "#E69F00",  // orange
  "N2O": "#D55E00", // vermillion
  "CO2": "#0072B2",  // blue
  "WTT-CO2eq": "#009E73", // bluish-green
  // fall-back default
  default: "#8884d8"
};

const INTERPRETATIONS = [
  { id: "IMO", item: "IMO" },
  { id: "FEUM", item: "FEUM" },
  { id: "ETS", item: "ETS" },
  { id: "CII", item: "CII" },
  { id: "STACK", item: "STACK" }
];

/* ================================================================== */
/*  Component                                                         */
/* ================================================================== */
const EmissionTab = ({ activeTab, showBtn = true }) => {
  const dispatch = useDispatch();

  /* Local state ---------------------------------------------------- */
  const [labels, setLabels] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [chartKey, setChartKey] = useState(0);
  const [show, setShow] = useState(false);
  const [interpretation, setInterpretation] = useState(INTERPRETATIONS[0]);

  /* Redux selectors ------------------------------------------------ */
  const checked = useSelector((s) => s.emissionSwitchChecked);
  const { fromYear } = useSelector((s) => s.selectedYearItem);
  const emission = useSelector((s) => s.pdfData);
  const legislativeScenarioName = emission?.legislativeScenarioName;
  const pricingScenarioName = emission?.priceScenarioName;
  const metaReady = legislativeScenarioName && pricingScenarioName != null;

  /* Build datasets whenever inputs change -------------------------- */
  useEffect(() => {
    const root = emission?.myResultsEmissions?.myResultsEmissions;
    const interpKey = interpretation.id;
    if (!root || !interpKey || !root[interpKey]) {
      setLabels([]);
      setDatasets([]);
      return;
    }
    const interpretationData = root[interpKey];

    const yLabels = Object.keys(interpretationData);

    const pollutantNames = Array.from(
      new Set(
        Object.values(interpretationData).flatMap((vesselObj) =>
          Object.keys(vesselObj)
        )
      )
    );

    /* Helper: decide colour --------------------------------------- */
    const chooseColor = (pollutant, sampleRec) => {
      // precedence: fixed palette  ►  server colour  ►  default
      if (POLLUTANT_COLORS[pollutant]) return POLLUTANT_COLORS[pollutant];
      if (sampleRec?.renderStyle?.myColor) return sampleRec.renderStyle.myColor;
      return POLLUTANT_COLORS.default;
    };

    const newDatasets = pollutantNames.map((pollutant) => {
      const quantitySeries = yLabels.map((v) =>
        interpretationData[v][pollutant]
          ? Number(interpretationData[v][pollutant].quantityEmittedMt.toFixed(2))
          : 0
      );
      const co2eqSeries = yLabels.map((v) =>
        interpretationData[v][pollutant]
          ? Number(interpretationData[v][pollutant].co2eq_EmittedMt.toFixed(2))
          : 0
      );

      // find first non-undefined record for palette fallback
      const firstRec = yLabels
        .map((v) => interpretationData[v][pollutant])
        .find(Boolean);

      const baseColor = chooseColor(pollutant, firstRec);

      return {
        label: pollutant,
        data: checked ? co2eqSeries : quantitySeries,
        quantityEmittedMt: quantitySeries,
        co2eq_EmittedMt: co2eqSeries,
        backgroundColor: quantitySeries.map((qty) =>
          qty < 0 ? pattern.draw("dash", baseColor) : baseColor
        ),
        barThickness: 10,
        borderWidth: 0,
        barPercentage: 0.6,
        categoryPercentage: 0.4,
      };
    });

    setLabels(yLabels);
    setDatasets(newDatasets);
  }, [emission, interpretation, checked]);

  /* Force re-render on tab switch --------------------------------- */
  useEffect(() => {
    if (activeTab === 4) setChartKey((k) => k + 1);
  }, [activeTab]);

  /* Chart.js config ------------------------------------------------ */
  const chartData = { labels, datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    scales: {
      x: {
        stacked: true,
        grid: { display: true },
        title: {
          display: true,
          text: `Emissions [mt${checked ? " CO₂eq" : ""}]`,
          font: { size: 15, weight: "bold", family: "Cabin, sans-serif" },
          padding: { top: 30 },
        },
        ticks: {
          color: "#212529",
          font: { size: 15, family: "Cabin, sans-serif" },
        },
      },
      y: {
        stacked: true,
        grid: { display: true },
        ticks: {
          crossAlign: "far",
          color: "#000",
          padding: 20,
          font: { size: 15, weight: "bold", family: "Cabin, sans-serif" },
          autoSkip: false,
        },
        border: { display: false },
      },
    },
    layout: { padding: { top: 75 } },
    plugins: {
      datalabels: { display: false },
      tooltip: {
        position: "myCustomPositionerLeft",
        displayColors: false,
        callbacks: {
          label: (ctx) => {
            if (!ctx) return "";
            const i = ctx.dataIndex;
            return [
              `Quantity: ${formatNumber(ctx.dataset.quantityEmittedMt[i])} mt`,
              `CO₂-eq: ${formatNumber(ctx.dataset.co2eq_EmittedMt[i])} mt`,
            ];
          },
          title: (ctx) =>
            ctx?.[0] ? `${ctx[0].label} – ${ctx[0].dataset.label}` : "",
        },
      },
      legend: {
        position: "bottom",
        labels: {
          padding: 40,
          color: "#212529",
          font: { size: 15, family: "Cabin, sans-serif" },
        },
      },
    },
  };

  /* Misc. refs for scrolling / modal ------------------------------ */
  const containerRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (show) containerRef.current.scrollIntoView({ behavior: "auto" });
  }, [show]);

  const handleCloseModal = () => {
    setShow(false);
    chartRef?.current?.scrollIntoView({ behavior: "auto" });
  };

  /* Dynamic height ------------------------------------------------- */
  const barHeight = 50;
  const extraPadding = 200;
  const chartHeight = labels.length * barHeight + extraPadding;

  /* Render --------------------------------------------------------- */
  return (
    <div ref={containerRef}>
      {/* Header --------------------------------------------------- */}
      <div className="tab-content-title d-flex justify-content-between flex-wrap align-items-start">
        <div>
          <h2>
            Emissions <span className="fs-4">{fromYear.item}</span>
          </h2>
          <div className="fs-6">
            {metaReady ? (
              <>
                <span className="d-block">
                  <span className="fw-normal">Legislative Scenario:</span>{" "}
                  <span className="fw-bold">{legislativeScenarioName}</span>
                </span>
                <span className="d-block">
                  <span className="fw-normal">Pricing Scenario:</span>{" "}
                  <span className="fw-bold">{pricingScenarioName}</span>
                </span>
              </>
            ) : (
              <span className="text-muted">Loading Results</span>
            )}
          </div>
        </div>

        {showBtn && (
          <div className="d-flex flex-wrap align-items-center gap-3 mt-2 mt-lg-0">
            {/* Interpretation selector */}
            <SelectBox
              selectedItem={interpretation}
              listItems={INTERPRETATIONS}
              setSelectedItem={setInterpretation}
              showSelect={false}
              showErrorPopup={false}
              selectBoxContainer="fuelUnit-container w-10rem"
            />

            {/* mt / CO₂-eq toggle */}
            <div className="form-check form-switch ft-1rem d-flex align-items-center gap-2">
              <span className="text-nowrap">mt Pollutant</span>
              <input
                className="form-check-input cursor-pointer no-dim-toggle"
                type="checkbox"
                id="custom-switch"
                checked={checked}
                onChange={(e) => dispatch(setChecked(e.target.checked))}
                style={{ marginLeft: 5 }}
              />
              <span className="text-nowrap">mt CO₂-eq</span>
            </div>

            {/* Notes & Sources */}
            <button className="btn btn-primary" onClick={() => setShow(true)}>
              Notes and Sources
            </button>
          </div>
        )}
      </div>

      {/* Chart ---------------------------------------------------- */}
      <div className="fleet-chart-container h-auto">
        {datasets.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center h-100">
            <Loading loading />
          </div>
        ) : (
          <div
            style={{
              width: "100%",
              height: `${chartHeight}px`,
              position: "relative",
            }}
          >
            <Bar data={chartData} options={options} key={chartKey} />
          </div>
        )}
      </div>

      {/* Modal ---------------------------------------------------- */}
      <InfoModal
        ref={chartRef}
        show={show}
        handleClose={handleCloseModal}
        data={emission?.myResultsEmissions?.comments}
      />
    </div>
  );
};

export default EmissionTab;
